/**
 * AddItemGroups — PR-6 composite step 3 of the Add Item wizard.
 *
 * Replaces the old Category → Product → Sqft screens with a per-sub-area
 * group builder. Each selected sub-area becomes a card:
 *   - collapsed: "+ Add Item" (or "+ Add another item" if it already has lines)
 *   - active:    full builder (Category → Product → measurement + extras)
 *   - committed lines render inline above the active area, each with ⋮
 *     (Edit · Add alternative · Delete).
 *
 * Wires:
 *   - QB-W-add-item-wizard-STATES.html (W2→W3→W4→W5→W6→W7)
 *   - QB-LINE-line-item-accessories-FLOW.html (per-line affordances)
 *
 * State: local useReducer over addItemGroupsState.js (pure).
 * The parent (`AddItemCascade`) owns:
 *   - accessoryPickerCtx (which draft is currently editing accessories)
 *   - altModalCtx        (which committed line is receiving an alternative)
 * so nested Slideovers can be sibling-rendered instead of nested.
 */

import React, { useReducer, useEffect, useCallback, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/20/solid';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import SelectMenu from '../../ui/SelectMenu';
import NoteTemplateMenu from './NoteTemplateMenu';
import {
  PRODUCT_CATEGORIES,
  getMeasurementLabel,
  synthesizeTenantProduct,
  disambiguateProductLabels,
  titleCaseCategoryId,
  materialPricePerSqft,
} from '../../../quote-builder/seedCatalog';
import {
  reducer,
  initialState,
  hasDirtyDraft,
  commitReady,
  isDraftComplete,
} from '../../../quote-builder/addItemGroupsState';
import InlineAccessoryRows from './InlineAccessoryRows';
import CommittedLineCard from './CommittedLineCard';
import { coverageOptions, surfaceForSubArea, isOfferableOnSurface } from '../../../quote-builder/productCoverage';
import { computeLineItem } from '../../../quote-builder';

// ─────────────────────────────────────────────────────────────────────────
// Small building blocks
// ─────────────────────────────────────────────────────────────────────────

// IDI-366: the Add Item row's column tracks. minmax(0, …) lets a track shrink
// below its content (a plain 1fr won't, so a long product name pushed Sq ft to
// nothing); Sq ft never drops below a width that shows a 5-digit value.
export const SQFT_MIN_WIDTH = '6.5rem';
export function addItemGridColumns(hasCoverage) {
  return hasCoverage
    ? `minmax(0, 1fr) minmax(0, 1.25fr) minmax(0, 1fr) minmax(${SQFT_MIN_WIDTH}, 0.75fr)`
    : `minmax(0, 1fr) minmax(0, 1.25fr) minmax(${SQFT_MIN_WIDTH}, 0.75fr)`;
}

function ReadOnlyPill({ label, value }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
      style={{
        backgroundColor: 'var(--neutral-100)',
        color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)',
      }}
      title="Catalog-locked — not editable"
    >
      <span style={{ opacity: 0.7 }}>{label}:</span>
      <strong>{value}</strong>
    </span>
  );
}
// ─────────────────────────────────────────────────────────────────────────
// GroupCard: one card per sub-area
// ─────────────────────────────────────────────────────────────────────────

function GroupCard({
  group,
  areaId,
  nudge,          // nudge scoped to THIS group (or null)
  onActivate,
  onUpdateDraft,
  onCancelEdit,
  onEditLine,
  onDeleteLine,
  onOpenAccessoriesForDraft,
  onOpenAltForLine,
  // PT-30 (2026-07-27, follow-up to 30.2 ruling): full margin context threaded
  // from QuoteBuilderPage → AddItemCascade → AddItemGroups → here so the
  // wizard's committed-line preview cards resolve customer + customer-type
  // levels, not just line/category/quote. When absent (isolated tests, or
  // callers that predate this prop) the engine falls back through the
  // marginContext's own `overallMargin` alias or line-level defaults.
  marginContext,
  // PR-66 s2c (Q-11): tenant Quote Items (insulation subset). Merged into
  // this GroupCard's Category + Product droplists. Default [] so isolated
  // callers (tests, older parents) still work — static-only fallback.
  tenantQuoteItems = [],
}) {
  const isActive = group.mode === 'active';
  const draft = group.activeDraft;
  const committed = group.committedLines;
  const editing = Boolean(draft && draft._editingLineId);

  // PR-66 s2c (Q-11): merge static PRODUCT_CATEGORIES with any tenant-only
  // categoryIds seen in `tenantQuoteItems`. `staticCategoryIds` is precomputed
  // once per render; new tenant-only categories get a sensible default label
  // (title-cased categoryId) and defaultMargin=0.30 until Adrian rules on the
  // margin default for tenant-only categories.
  const staticCategoryIds = new Set(PRODUCT_CATEGORIES.map((c) => String(c.id)));
  const tenantOnlyCategoryIds = Array.from(
    new Set(
      (tenantQuoteItems || [])
        .map((r) => r && r.categoryId)
        .filter((cid) => cid != null && !staticCategoryIds.has(String(cid)))
    )
  );
  const mergedCategories = [
    ...PRODUCT_CATEGORIES.filter((c) => c.id !== 'accessories'),
    ...tenantOnlyCategoryIds.map((cid) => ({
      // Portica ruling 391 §1: the tenant categoryId may be numeric on
      // migrated tenants. `cid.split('_')` crashes on a Number (TypeError,
      // white screen on the Add Item panel). titleCaseCategoryId coerces
      // internally and is the single writer for the label rule so this
      // string matches whatever buildLineItem writes onto the line's
      // categoryLabel field (which flows to quote docs / change orders /
      // work orders). We also coerce the `id` so downstream draft state
      // and equality checks see a stable string type.
      id: String(cid),
      label: titleCaseCategoryId(cid),
      // Portica ruling 390 §6a / 391 §2 note: this defaultMargin isn't
      // actually read by buildLineItem — mergedCategories never reaches it,
      // and buildLineItem's own category lookup returns null (via
      // resolveTenantCategory) for tenant-only categoryIds. The cascade
      // works because `category?.defaultMargin` is undefined, not because
      // this field is null. Keep the null anyway — it's the correct value
      // for a tenant-only category and prevents future readers from
      // silently getting a fabricated 0.30 if they ever start reading it.
      // `marginKey: null` gap is 382 §2.1 / PR-69, not this PR.
      defaultMargin: null,
      marginKey: null,
    })),
  ];

  // IDI-429 (ruled 2026-09-29): list ONLY categories that can offer at least
  // one product, from the SAME source the product droplist below uses:
  // the tenant rows AddItemCascade already
  // narrowed to offerable (filterAreaPriceableRows, IDI-373). Before this the
  // category list ignored that filter, so seven categories (Foil Batts, FSK
  // Batts, HD FG Board, Light Covers, MBI Rolls, Min Wool Blow, Safing)
  // opened to "No options". A category reappears the moment one of its
  // products gains a price and coverage. The draft's current category is
  // always kept, so an edit of a line never loses its own category.
  // IDI-136 (D1/D2): the built-in sample products are gone, so only a
  // category with a dealer catalog row is offered; otherwise "No options".
  const offerableCategoryIds = new Set([
    ...(tenantQuoteItems || []).map((r) => r && r.sku != null && r.categoryId).filter((c) => c != null && c !== false).map(String),
  ]);
  const offerableCategories = mergedCategories.filter(
    (c) => c.id !== 'accessories' && (offerableCategoryIds.has(String(c.id)) || String(c.id) === String(draft?.categoryId))
  );

  // IDI-136: products come ONLY from the dealer's catalog rows in this
  // category (the 25 built-ins are retired). Tenant rows keyed by sku are
  // surfaced as `{ id: sku, label: name, ... }` shims via the canonical
  // synthesizeTenantProduct; buildLineItem resolves the sku through the
  // `tenantProducts` arg threaded from AddItemCascade. A saved line whose
  // productId is a retired built-in ("kraft_r13") finds no row here, so
  // Edit shows Product "— Select —" and Add refuses it (IDI-365 message).
  const tenantProductsForCategory = (tenantQuoteItems || [])
    .filter(
      (r) =>
        r &&
        r.sku != null &&
        // Portica ruling 391 §1: coerce both sides (see history above).
        String(r.categoryId) === String(draft?.categoryId)
    )
    // Portica ruling 390 §3: use the canonical synthesizeTenantProduct so this
    // shape lives in one place. AddItemGroups previously duplicated the field
    // mapping by hand — an N-14 violation that let §2 (labor: 0 lie) exist in
    // two copies.
    .map((r) => synthesizeTenantProduct(r))
    .filter(Boolean);
  // IDI-208 (2026-08-31 audit finding): disambiguate any duplicate-name
  // collision within this category's product list before it reaches the
  // droplist -- see disambiguateProductLabels for the full rationale
  // (33.9% of active pricebook rows share a name with a sibling). No-op
  // for the static seed catalog and for the vast majority of real rows
  // that don't collide.
  // IDI-173: the surface this sub-area insulates picks the attic vs wall chart.
  const coverageSurface = surfaceForSubArea(areaId, group.subAreaName);
  // IDI-434 (ruled): a chart product with no chart for THIS surface is not
  // offered (an attic chart never stands in for a wall chart). The draft's
  // own product is kept so editing an existing line never loses it.
  const availableProducts = disambiguateProductLabels(tenantProductsForCategory)
    .filter((p) => isOfferableOnSurface(p, coverageSurface) || String(p.id) === String(draft?.productId));

  const selectedProduct = draft?.productId
    ? availableProducts.find((p) => String(p.id) === String(draft.productId)) || null
    : null;
  const coverageOpts = coverageOptions(selectedProduct, coverageSurface);
  // IDI-173: tell the reducer how many coverage entries this product has, so
  // isDraftComplete blocks Save / Add until one is chosen (only when > 1).
  React.useEffect(() => {
    if (!draft) return;
    const n = coverageOpts.length;
    if ((draft._coverageOptionCount || 0) !== n) onUpdateDraft({ _coverageOptionCount: n });
    if (n === 1 && draft.coverageChoice !== coverageOpts[0].value) onUpdateDraft({ coverageChoice: coverageOpts[0].value });
  }, [draft?.productId, coverageOpts.length]); // eslint-disable-line react-hooks/exhaustive-deps -- re-sync only when the product or its option count changes

  const measurementLabel = getMeasurementLabel(selectedProduct);

  // PR-66 deploy-gate presence anchor. Value distinguishes:
  //   "settings" - tenant Quote Items contributed at least one product or
  //                category to this GroupCard's current droplists
  //   "static"   - only static seedCatalog contributed (fallback / empty
  //                tenant list / fetch failure)
  // Probe: grep -c 'data-portica-role="quote-items-source"' <served bundle>
  const quoteItemsSource =
    tenantOnlyCategoryIds.length > 0 || tenantProductsForCategory.length > 0
      ? 'settings'
      : 'static';

  return (
    <div
      className="rounded-lg border mb-3"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
      data-portica-role="quote-items-source"
      data-quote-items-source={quoteItemsSource}
    >
      {/* Header row (always visible) */}
      <div className="flex items-center gap-2 px-4 py-3">
        <div className="flex-1">
          <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            {group.subAreaName}
          </div>
          <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {committed.length === 0
              ? 'No items yet'
              : `${committed.length} item${committed.length > 1 ? 's' : ''}`}
          </div>
        </div>
        {!isActive && (
          <button
            type="button"
            onClick={onActivate}
            className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-gray-50"
            style={{ color: 'var(--color-action-primary)' }}
          >
            <PlusIcon className="h-4 w-4" />
            {committed.length === 0 ? 'Add Item' : 'Add another item'}
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}
        {isActive && (
          <ChevronDownIcon className="h-4 w-4" style={{ color: 'var(--color-text-secondary)' }} />
        )}
      </div>

      {/* Nudge scoped to this group (blocking-group indicator). */}
      {nudge && (
        <div
          className="mx-4 mb-2 px-3 py-2 text-xs rounded"
          style={{ backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}
          role="status"
        >
          {nudge.message}
        </div>
      )}

      {/* Committed lines (always shown above active area) */}
      {committed.length > 0 && (
        <div>
          {committed.map((line) => {
            // PT-30 (2026-07-27): prefer the fresh marginContext so this preview
            // matches the Estimate Panel truth-of-record for the currently
            // selected customer + customer type. Falls back to the line's own
            // frozen `overallMargin` snapshot when marginContext is unavailable
            // (isolated tests, or callers that predate the prop).
            const computed = computeLineItem(
              line,
              marginContext !== undefined ? marginContext : (line.overallMargin || 0),
            );
            return (
              <CommittedLineCard
                key={line.id}
                item={line}
                computed={computed}
                onEdit={() => onEditLine(line.id)}
                onAddAlternate={onOpenAltForLine ? () => onOpenAltForLine(line) : undefined}
                onRemove={() => onDeleteLine(line.id)}
              />
            );
          })}
        </div>
      )}

      {/* Active builder */}
      {isActive && draft && (
        <div
          className="px-4 py-3 border-t"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--neutral-50)' }}
        >
          {editing && (
            <div className="mb-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              Editing existing item — Save item overwrites.
            </div>
          )}

          {/* PR-10: Category / Product / Measurement on one row (wire .g3 { 1fr 1.25fr 0.75fr }).
              All three cells always render; Product and Sq ft are disabled until their
              prerequisites are met so the grid shape stays stable through the flow. */}
          {/* IDI-366 (RULED 10-13: truncate, don't wrap): every track is
              minmax(0, …) and every cell min-w-0, so a long selected product
              name truncates (SelectMenu ellipsis + full name on hover) instead
              of widening its column. Coverage gets its own track when shown
              (it used to wrap onto a 4th cell of a 3-column grid), and Sq ft
              keeps a usable minimum. */}
          <div
            className="grid gap-2.5 mb-3"
            data-testid="add-item-fields"
            style={{ gridTemplateColumns: addItemGridColumns(coverageOpts.length > 0) }}
          >
            {/* Category */}
            <div className="min-w-0">
              <label htmlFor={`category-${group.subAreaId}`} className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Category
              </label>
              <SelectMenu
                id={`category-${group.subAreaId}`}
                aria-label={`Category for ${group.subAreaName}`}
                value={draft.categoryId || ''}
                placeholder="— Select —"
                width="full"
                onChange={(next) => onUpdateDraft({
                  categoryId: next || null,
                  productId: null, // reset dependent selection
                })}
                options={offerableCategories.map((c) => ({ value: c.id, label: c.label }))}  /* PT-15 #9 (2026-07-24): hide Accessories from the category droplist — accessories now attach on each line via the accessory picker. Do NOT delete the seed entry: it still carries the accessories marginKey (IDI-309: there is no seed margin any more). */
                /* PR-66 s2c (Q-11): mergedCategories = static PRODUCT_CATEGORIES
                   + any tenant-only categoryIds seen in settings.quote_items. */
              />
            </div>

            {/* Product — disabled until category picked */}
            <div className="min-w-0">
              <label htmlFor={`product-${group.subAreaId}`} className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Product
              </label>
              <SelectMenu
                id={`product-${group.subAreaId}`}
                aria-label={`Product for ${group.subAreaName}`}
                value={draft.productId || ''}
                placeholder={draft.categoryId ? '— Select —' : '— Pick category first —'}
                width="full"
                disabled={!draft.categoryId}
                onChange={(next) => onUpdateDraft({ productId: next || null, coverageChoice: null })}
                options={availableProducts.map((p) => ({ value: p.id, label: p.label }))}
              />
            </div>

            {/* IDI-173: Coverage, after Product, for every product with a
                chart (all blow-in; spray foam by thickness). Lists ALL and
                ONLY the entries with a coverage value for this product, for
                this sub-area's surface (attic vs wall). */}
            {coverageOpts.length > 0 && (
              <div className="min-w-0">
                <label htmlFor={`coverage-${group.subAreaId}`} className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Coverage{coverageSurface === 'wall' ? ' (wall)' : coverageOpts[0]?.unitLabel === 'bag' ? ' (attic)' : ''}
                </label>
                <SelectMenu
                  id={`coverage-${group.subAreaId}`}
                  aria-label={`Coverage for ${group.subAreaName}`}
                  value={draft.coverageChoice || ''}
                  placeholder="— Select —"
                  width="full"
                  onChange={(next) => onUpdateDraft({ coverageChoice: next || null })}
                  options={coverageOpts.map((o) => ({
                    value: o.value,
                    label: `${o.label} · ${Math.round(o.sqftPerUnit * 100) / 100} sq ft/${o.unitLabel}`,
                  }))}
                />
              </div>
            )}

            {/* Measurement — disabled until product picked; label follows catalog unit */}
            <div className="min-w-0">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                {draft.productId ? measurementLabel : 'Sq ft'}
              </label>
              <Input
                type="number"
                inputMode="decimal"
                value={draft.sqft}
                onChange={(e) => onUpdateDraft({ sqft: e.target.value })}
                placeholder="0"
                min="0"
                disabled={!draft.productId}
              />
            </div>
          </div>

          {/* PR-10: Locked catalog metadata pills on their own full-width row below the grid
              (R4/R17: catalog cost + R-value shown but not editable).
              PR-66 s2c (Portica ruling 390 §4): the pill label depends on whether
              product-tier labor exists. Static products with a rate combine
              material + labor as "Catalog cost". Products with laborCostPerUnit
              == null (tenant Quote Items, or static rows deliberately deferring
              to the tier chain) show "Material" only — the tier chain resolves
              labor separately, and pretending the catalog priced it (as the
              old sum did) would under-report by the labor portion. The
              predicate here (!= null) mirrors resolveLaborCost.js:74 so pill
              text and tier-3 firing are the same truth. */}
          {selectedProduct && (() => {
            // IDI-373 (label): show the price PER SQ FT that the line is
            // actually charged (materialPricePerSqft follows buildLineItem's
            // branches). A per-unit pricebook row (bag / bundle / set) used to
            // print its per-UNIT cost with "/sq ft": Kraft $40.11 per 148.54
            // sq ft read "$40.11/sq ft". Labor is already per sq ft
            // (resolveLaborCost multiplies by sqft), so it adds directly.
            const hasCatalogLabor = selectedProduct.laborCostPerUnit != null;
            const laborCents = hasCatalogLabor
              ? Number(selectedProduct.laborCostPerUnit) || 0
              : 0;
            // IDI-173: with a Coverage choice, the pill shows THAT entry's price.
            const chosenCoverage = coverageOpts.find((o) => o.value === draft.coverageChoice);
            if (coverageOpts.length > 1 && !chosenCoverage) {
              return (
                <div className="flex flex-wrap gap-1 mb-3 -mt-1">
                  <ReadOnlyPill label={hasCatalogLabor ? 'Catalog cost' : 'Material'} value="Choose a coverage" />
                </div>
              );
            }
            const material = materialPricePerSqft(selectedProduct, chosenCoverage?.sqftPerUnit ?? (coverageOpts.length === 1 ? coverageOpts[0].sqftPerUnit : null));
            const unit = selectedProduct.unit || 'sq ft';
            let pillValue;
            if (!material) {
              pillValue = 'Not priced';
            } else {
              const perSqft = material.perSqftCents + laborCents;
              // Sub-cent-per-sqft prices matter at this scale ($0.27 vs $0.3),
              // so always show cents; very small ones get a 3rd decimal.
              const dollars = perSqft / 100;
              const shown = dollars > 0 && dollars < 0.1 ? dollars.toFixed(3) : dollars.toFixed(2);
              pillValue = `$${shown}/${unit}`;
              if (material.coverage) {
                // Also say what the dealer actually buys, so $0.27 is traceable
                // to the $40.11 bundle, or a $25.00 bag covering 40 sq ft.
                const buys = (chosenCoverage || coverageOpts[0])?.unitLabel;
                pillValue += buys
                  ? ` ($${(material.perUnitCents / 100).toFixed(2)} per ${buys}, ${+material.coverage.toFixed(2)} ${unit}/${buys})`
                  : ` ($${(material.perUnitCents / 100).toFixed(2)} per ${+material.coverage.toFixed(2)} ${unit})`;
              }
            }
            return (
              <div className="flex flex-wrap gap-1 mb-3 -mt-1">
                <ReadOnlyPill
                  label={hasCatalogLabor ? 'Catalog cost' : 'Material'}
                  value={pillValue}
                />
                {selectedProduct.rValue && (
                  <ReadOnlyPill label="R-value" value={selectedProduct.rValue} />
                )}
              </div>
            );
          })()}

          {/* Note / additional labor / additional cost accordion */}
          {draft.productId && (
            <ExtrasAccordion draft={draft} onUpdateDraft={onUpdateDraft} />
          )}

          {/* Accessories — inline editable rows + picker trigger (PR-PT-03) */}
          {draft.productId && (
            <div className="mb-3">
              {draft.accessories && draft.accessories.length > 0 && (
                <InlineAccessoryRows
                  accessories={draft.accessories}
                  onChange={(next) => onUpdateDraft({ accessories: next })}
                />
              )}
              {/* Roll-up readout */}
              {draft.accessories && draft.accessories.length > 0 && (() => {
                const rolledIn = (draft.accessories || []).filter(a => a.rollUp !== false)
                  .reduce((s, a) => s + (Number(a.qty) || 0) * (Number(a.unit_cost) || 0), 0);
                const itemised = (draft.accessories || []).filter(a => a.rollUp === false)
                  .reduce((s, a) => s + (Number(a.qty) || 0) * (Number(a.unit_cost) || 0), 0);
                return (rolledIn > 0 || itemised > 0) ? (
                  <div className="text-xs mt-1 mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    ${(rolledIn / 100).toFixed(2)} rolled in{itemised > 0 ? ` \u00b7 $${(itemised / 100).toFixed(2)} itemised` : ''}
                  </div>
                ) : null;
              })()}
              <Button
                size="sm"
                variant="tertiary"
                onClick={onOpenAccessoriesForDraft}
              >
                + Add accessory
              </Button>
            </div>
          )}

          {/* Active-group footer buttons. */}
          <div className="flex justify-end gap-2 mt-2">
            {editing && (
              <Button variant="secondary" size="sm" onClick={onCancelEdit}>Cancel</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ExtrasAccordion (note / addl labor / addl cost)
// ─────────────────────────────────────────────────────────────────────────

function ExtrasAccordion({ draft, onUpdateDraft }) {
  const hasContent =
    (draft.note && draft.note.length > 0) ||
    (draft.addlLabor !== '' && draft.addlLabor != null) ||
    (draft.addlCost !== '' && draft.addlCost != null) ||
    (draft.alias && draft.alias.length > 0);
  const [open, setOpen] = useState(hasContent);
  return (
    <div className="mb-3">
      <Button
        size="sm"
        variant="tertiary"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '− Hide' : '+ Alias · note · additional labor / cost'}
      </Button>
      {open && (
        <div className="mt-2 space-y-2">
          {/* PR-62 §3aj: Alias — free-text per-line display-name override for
              the customer document. When set, renders in QuoteSelectionPanel
              in place of item_name. Internal surfaces (AreaCard, QuoteDetail,
              EstimatePanel) always show item_name and never the alias. See
              quote-builder/optionLabel.js VOCAB block. */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Alias (customer document only)
            </label>
            <Input
              value={draft.alias || ''}
              onChange={(e) => onUpdateDraft({ alias: e.target.value })}
              placeholder="e.g. Playroom / Storage Room"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Note
              </label>
              {/* PR-65 Q-03 (2026-08-20): consumer of NoteTemplateMenu after
                  R-25 (quote-level notes on QuoteBuilderPage) and G-20 (line-item
                  note — originally on EditItemModal, now on THIS component's
                  ExtrasAccordion after IDI-176 retired that modal 2026-09-05).
                  Same component, same canonical field (`note`, singular) — the
                  shared abstraction pays out here. Insertion default: append
                  with blank line via onSelect. */}
              <div className="ml-auto">
                <NoteTemplateMenu
                  currentValue={draft.note || ''}
                  onSelect={(v) => onUpdateDraft({ note: v })}
                />
              </div>
            </div>
            <Input
              value={draft.note || ''}
              onChange={(e) => onUpdateDraft({ note: e.target.value })}
              placeholder="Optional note for this line"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Additional labor ($)
              </label>
              <Input
                type="number"
                inputMode="decimal"
                value={draft.addlLabor}
                onChange={(e) => onUpdateDraft({ addlLabor: e.target.value })}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Additional cost ($)
              </label>
              <Input
                type="number"
                inputMode="decimal"
                value={draft.addlCost}
                onChange={(e) => onUpdateDraft({ addlCost: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// AddItemGroups — main component (imperative handle for parent Slideover footer)
// ─────────────────────────────────────────────────────────────────────────

const AddItemGroups = React.forwardRef(function AddItemGroups(
  {
    areaId,
    subAreas,
    buildLine,
    onCommit,
    onDirtyChange,
    onRequestAccessoryPicker,
    onRequestAltModal,
    // IDI-823 (D2): the document the items are added to ('quote' by default).
    targetNoun = 'quote',
    // PT-30 (2026-07-27, follow-up to 30.2 ruling): full margin context threaded
    // in from AddItemCascade so the wizard's committed-line preview cards
    // resolve customer + customer-type levels (not just line/category/quote).
    // Passed through unmodified to each `<GroupCard>`.
    marginContext,
    // PR-66 s2c (Q-11): tenant Quote Items (insulation subset) from
    // settings.quote_items, threaded from AddItemCascade. Each GroupCard
    // merges these into its Category + Product droplists so tenant products
    // appear alongside static PRODUCTS[]. Empty array is safe (static-only).
    tenantQuoteItems = [],
    // IDI-176: wizard-as-editor entry-point. When set, AddItemGroups
    // dispatches INIT_FOR_EDIT after subAreas initialization, seeding the
    // matching sub-area group with this line and activating an edit draft.
    // Shape: { subAreaId, line }. The `line` should be a full quote line-
    // item; `subAreaId` names which group receives it.
    //   - Undefined / null: normal add-mode entry (no seed).
    //   - subAreaId matches no group: silent no-op in the reducer (see
    //     addItemGroupsState.js INIT_FOR_EDIT for the failure-mode list).
    // Parent (AddItemCascade) is responsible for ensuring subAreaId is a
    // real group before opening the wizard in edit mode.
    initialEditItem,
  },
  ref
) {
  const [state, dispatch] = useReducer(reducer, subAreas, initialState);
  const [footerNudge, setFooterNudge] = useState(null); // "Save item first" nudge shown near Add-to-quote

  // Re-init if the subAreas list changes (e.g., user backed to step 2 and re-selected).
  //
  // IDI-365: keyed on the sub-area IDS, not the array. The parent rebuilds
  // `subAreas` whenever an async settings fetch lands (custom_areas, ~1 s
  // after the sheet opens). A new array with the same ids re-ran INIT_GROUPS
  // + INIT_FOR_EDIT and reset the edit draft from the SAVED line, silently
  // discarding whatever the user had typed (walk: 1500 sq ft edited to 777,
  // saved as 1500). A real change of sub-areas still re-inits.
  //
  // It also ignores a TRANSIENT empty list while editing: AddItemCascade
  // drops the merged sub-areas to [] while its sub_areas fetch reloads
  // (subAreasFetch LOADING), then restores them. Measured in a real browser
  // on dev: key "accessible_spf" -> "" -> "accessible_spf", each flip
  // re-seeding the draft from the saved line (the typed 777 became 1500).
  const rawSubAreaKey = (Array.isArray(subAreas) ? subAreas : []).map((sa) => String(sa?.id ?? '')).join('|');
  // The edit target can also blink to undefined during that reload (it is
  // derived from the same merged sub-areas), so latch the last real one.
  const lastEditItemRef = React.useRef(null);
  if (initialEditItem && initialEditItem.line && 'subAreaId' in initialEditItem) {
    lastEditItemRef.current = initialEditItem;
  } else if (initialEditItem === null) {
    lastEditItemRef.current = null;
  }
  const effectiveEditItem = lastEditItemRef.current;
  const editingLineId = effectiveEditItem ? effectiveEditItem.line.id : null;
  const stableKeyRef = React.useRef(rawSubAreaKey);
  if (!(editingLineId && rawSubAreaKey === '' && stableKeyRef.current !== '')) {
    stableKeyRef.current = rawSubAreaKey;
  }
  const subAreaKey = stableKeyRef.current;
  const latestSubAreasRef = React.useRef(subAreas);
  if (subAreaKey === rawSubAreaKey) latestSubAreasRef.current = subAreas;
  useEffect(() => {
    dispatch({ type: 'INIT_GROUPS', subAreas: latestSubAreasRef.current });
  }, [subAreaKey]);

  // IDI-176 · wizard-as-editor: after INIT_GROUPS has run (fresh state,
  // empty committedLines everywhere), seed the target group with the
  // line-being-edited and activate an edit draft on it. Runs whenever
  // `initialEditItem` changes — a fresh reference means a fresh edit
  // session; unchanged reference is a no-op because React skips the
  // effect. Requires that INIT_GROUPS has just run (which it did in the
  // effect above; both effects fire in the same render batch, so
  // INIT_GROUPS lands first).
  // IDI-365: seed the edit draft ONCE per (line, sub-area set). AddItemCascade
  // rebuilds `initialEditItem` (a new object) whenever its merged sub-areas
  // recompute -- e.g. when the async custom_areas fetch lands ~1 s after the
  // sheet opens. Re-seeding on that reset the draft from the SAVED line and
  // discarded what the user had typed. Key on identity, not reference.
  // The line id alone identifies the session (the resolved subAreaId also
  // blinks to undefined while the sub-areas reload); a real sub-area change
  // is covered by subAreaKey.
  const editSeedKey = editingLineId ? `${editingLineId}::${subAreaKey}` : '';
  const latestEditItemRef = React.useRef(effectiveEditItem);
  latestEditItemRef.current = effectiveEditItem;
  useEffect(() => {
    const item = latestEditItemRef.current;
    // IDI-328: `'subAreaId' in item` (existence, not truthiness) so an
    // Options-area line's genuine subAreaId: null is accepted.
    if (editSeedKey && item) {
      dispatch({
        type: 'INIT_FOR_EDIT',
        subAreaId: item.subAreaId,
        initialLine: item.line,
      });
    }
  }, [editSeedKey]);

  // Emit dirty state upward so the parent Slideover footer can disable Add-to-quote nudge etc.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(hasDirtyDraft(state));
  }, [state, onDirtyChange]);

  const handleActivate = useCallback((subAreaId) => {
    dispatch({ type: 'ACTIVATE_GROUP', subAreaId });
  }, []);

  const handleUpdateDraft = useCallback((subAreaId, patch) => {
    dispatch({ type: 'UPDATE_DRAFT', subAreaId, patch });
  }, []);

  const handleCancelEdit = useCallback((subAreaId) => {
    dispatch({ type: 'CANCEL_EDIT', subAreaId });
  }, []);

  const handleEditLine = useCallback((subAreaId, lineId) => {
    dispatch({ type: 'START_EDIT', subAreaId, lineId });
  }, []);

  const handleDeleteLine = useCallback((subAreaId, lineId) => {
    dispatch({ type: 'DELETE_LINE', subAreaId, lineId });
  }, []);

  // Parent will call this via ref when the Add-to-quote button is clicked.
  const commit = useCallback(() => {
    // GP-17: "Add to Quote" commits everything including unsaved complete drafts.
    if (hasDirtyDraft(state)) {
      const dirtyGroup = state.groups.find((g) => g.mode === 'active' && g.activeDraft);
      if (dirtyGroup && !isDraftComplete(dirtyGroup.activeDraft)) {
        setFooterNudge({
          message: `Complete the item (category, product, quantity) before adding to ${targetNoun}`,
        });
        return false;
      }
    }
    const groups = commitReady(state, buildLine);
    if (onCommit) onCommit(groups);
    return true;
  }, [state, onCommit, buildLine, targetNoun]);

  // Expose imperative API to parent Slideover footer.
  React.useImperativeHandle(ref, () => ({
    commit,
    hasDirtyDraft: () => hasDirtyDraft(state),
    /** Called by the parent when the sibling AccessoryPicker returns a new accessory list. */
    applyAccessoriesToActiveDraft: (subAreaId, nextAccessories) => {
      dispatch({ type: 'UPDATE_DRAFT', subAreaId, patch: { accessories: nextAccessories } });
    },
    /** Called by parent after AddAlternateModal commits an alternative. */
    applyAlternativeToLine: (subAreaId, lineId, alternative) => {
      dispatch({ type: 'ADD_ALTERNATIVE', subAreaId, lineId, alternative });
    },
  }), [commit, state]);

  const openAccessoriesForDraft = useCallback((group) => {
    if (!onRequestAccessoryPicker) return;
    // PR-21 (2026-07-23): include the active draft's sqft so the picker can
    // suggest coverage-based qty (ceil(sqft / coverage_sqft)). Prior to this,
    // the picker always fell back to qty=1 because the caller never provided
    // the parent line's sqft. Filed as tech-debt during the 10-PR run.
    const sqft = Number.parseFloat(group.activeDraft?.sqft);
    onRequestAccessoryPicker({
      subAreaId: group.subAreaId,
      productId: group.activeDraft?.productId,
      categoryId: group.activeDraft?.categoryId,
      accessories: group.activeDraft?.accessories || [],
      sqft: Number.isFinite(sqft) && sqft > 0 ? sqft : null,
    });
  }, [onRequestAccessoryPicker]);

  const openAltForLine = useCallback((group, line) => {
    if (!onRequestAltModal) return;
    onRequestAltModal({
      subAreaId: group.subAreaId,
      lineId: line.id,
      parentLine: line,
    });
  }, [onRequestAltModal]);

  // Clear footer nudge whenever state changes (user did something) — keep it terse.
  useEffect(() => {
    if (footerNudge && !hasDirtyDraft(state)) setFooterNudge(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <div className="space-y-2">
      <div className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
        Build one or more line items per sub-area. Save each item, then Add to {targetNoun}.
      </div>

      {state.groups.map((group) => (
        <GroupCard
          key={group.subAreaId}
          group={group}
          areaId={areaId}
          nudge={
            // Show nudge under the blocking group (state.nudge.subAreaId).
            state.nudge && state.nudge.subAreaId === group.subAreaId ? state.nudge : null
          }
          onActivate={() => handleActivate(group.subAreaId)}
          onUpdateDraft={(patch) => handleUpdateDraft(group.subAreaId, patch)}
          onCancelEdit={() => handleCancelEdit(group.subAreaId)}
          onEditLine={(lineId) => handleEditLine(group.subAreaId, lineId)}
          onDeleteLine={(lineId) => handleDeleteLine(group.subAreaId, lineId)}
          onOpenAccessoriesForDraft={() => openAccessoriesForDraft(group)}
          /* IDI-823 (D3): no handler, no "Add alternative" (CommittedLineCard hides it). */
          onOpenAltForLine={onRequestAltModal ? (line) => openAltForLine(group, line) : undefined}
          marginContext={marginContext}
          tenantQuoteItems={tenantQuoteItems}
        />
      ))}

      {footerNudge && (
        <div
          data-testid="footer-nudge"
          className="mt-2 px-3 py-2 text-xs rounded"
          style={{ backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}
        >
          {footerNudge.message}
        </div>
      )}
    </div>
  );
});

export default AddItemGroups;
